import { action, runInAction, extendObservable } from "mobx";

import { ResourceRequester } from "sulu-admin-bundle/services/index.js";

class PlayerStore {
    constructor() {
        extendObservable(this, {
            resourceStore: null,
            allPlayers: [],
            allPositions: [],
            disabledPositions: {},
            teamId: null,
            numberOfStartingPlayers: 0,
            allTeamPlayers: {},

            get startingPlayersCount() {
                return this.teamPlayers.filter((p) => p.isStartingPlayer)
                    .length;
            },

            get teamPlayers() {
                return this.allPlayers.filter((p) => p.team === this.teamId);
            },

            get goalkeeperPosition() {
                return this.allPositions.find(
                    (pos) => pos.positionType === "isGoalkeeper",
                );
            },
        });
    }

    loadTeam = action(async (teamId, locale) => {
        const response = await ResourceRequester.getList("dhbteam", {
            locale: locale || undefined,
            id: teamId,
        });

        runInAction(() => {
            this.allTeamPlayers = response.allTeamPlayers;
        });
    });

    syncSelection = action((selectedIds, teamId) => {
        const existingTeamPlayers = this.allPlayers.filter(
            (p) => p.team === teamId && !p.isDeleted,
        );

        const uniqueSelectedIds = [...new Set(selectedIds)];

        const removedIds = existingTeamPlayers
            .filter((p) => !uniqueSelectedIds.includes(p.contact))
            .map((p) => p.contact);

        const playersToAdd = [];

        uniqueSelectedIds.forEach((contact) => {
            const existing = existingTeamPlayers.find(
                (p) => p.contact === contact,
            );

            if (existing) {
                if (existing.isDeleted && existing.notSaved) {
                    delete existing.isDeleted;
                    delete existing.isChanged;
                } else {
                    delete existing.isDeleted;
                }
            } else {
                const teamPlayer = this.allTeamPlayers[contact];

                const alreadyExists = this.allPlayers.find(
                    (p) =>
                        p.contact === contact &&
                        p.team === teamId &&
                        !p.isDeleted,
                );

                if (alreadyExists) return;

                if (!teamPlayer) return;

                playersToAdd.push({
                    name: teamPlayer.name || "",
                    passportNumber: teamPlayer.passportNumber || "",
                    notSaved: true,
                    contact,
                    number: teamPlayer.number || 0,
                    isGoalkeeper: false,
                    isSubstituteGoalkeeper: false,
                    isCaptain: false,
                    passport: teamPlayer.passport,
                    team: teamId,
                    core: teamPlayer.core,
                    teamPositions:
                        teamPlayer.positions?.length > 0
                            ? teamPlayer.positions
                            : this.allPositions,
                });
            }
        });

        this.allPlayers.forEach((p) => {
            if (removedIds.includes(p.contact) && p.team === teamId) {
                if (
                    p.position &&
                    this.disabledPositions[teamId]?.includes(p.position)
                ) {
                    this.updateDisabledPosition(p.position, p.team, true);
                }

                p.isDeleted = true;
                p.isChanged = true;
                this.resetPlayer(p);
            }
        });

        const finalPlayers = new Map();

        this.allPlayers.forEach((p) => {
            finalPlayers.set(p.contact, p);
        });

        playersToAdd.forEach((p) => {
            if (!finalPlayers.has(p.contact)) {
                finalPlayers.set(p.contact, p);
            }
        });

        this.allPlayers = Array.from(finalPlayers.values());

        const cleanedFinal = this.allPlayers.filter((p) => {
            if (removedIds.includes(p.contact) && p.notSaved) return false;
            return true;
        });

        this.allPlayers = cleanedFinal;

        this.syncToResourceStore();
    });

    syncToResourceStore = action(() => {
        if (!this.resourceStore) return;
        runInAction(() => {
            this.resourceStore.change("allMatchPlayers", this.allPlayers);
            this.resourceStore.change("isSquadChange", true);
        });
    });

    removeDataChange = action(() => {
        if (!this.resourceStore) return;

        runInAction(() => {
            delete this.resourceStore.data.isSquadChange;
        });
    });

    removeNotSavedPlayers = action(() => {
        this.allPlayers = this.allPlayers.filter((p) => !p.notSaved);

        runInAction(() => {
            this.resourceStore.change("allMatchPlayers", this.allPlayers);
        });
    });

    setInitialData = action(
        async (players, positions, teamId, startingPlayers, resourceStore) => {
            this.allPlayers = players;
            this.allPositions = positions;
            this.teamId = teamId;
            this.numberOfStartingPlayers = startingPlayers;
            this.resourceStore = resourceStore;
            this.disabledPositions = {
                [teamId]: this.computeDisabledPositions(
                    players,
                    positions,
                    teamId,
                ),
            };
        },
    );

    computeDisabledPositions = action((players, positions, teamId) => {
        const disabled = [];
        players.forEach((p) => {
            if (p.team !== teamId) return;
            const pos = positions.find((pos) => pos.id === p.position);
            if (pos?.isUniquePerGame && !p.isDeleted) {
                disabled.push(p.position);
            }
        });
        return disabled;
    });

    updateDisabledPosition = action((positionId, teamId, remove = false) => {
        if (!this.disabledPositions[teamId]) {
            this.disabledPositions[teamId] = [];
        }

        if (remove) {
            this.disabledPositions[teamId] = this.disabledPositions[
                teamId
            ].filter((id) => id !== positionId);
        } else {
            if (!this.disabledPositions[teamId].includes(positionId)) {
                this.disabledPositions[teamId].push(positionId);
            }
        }
    });

    toggleGoalkeeper = action((playerId) => {
        const keeperPos = this.goalkeeperPosition;

        this.allPlayers.forEach((p) => {
            if (p.team !== this.teamId) return;

            if (p.contact === playerId) {
                if (p.position && keeperPos?.isUniquePerGame) {
                    this.updateDisabledPosition(p.position, p.team, true);
                }

                p.isGoalkeeper = true;
                p.position = keeperPos.id;
                p.isChanged = true;

                if (keeperPos?.isUniquePerGame) {
                    this.updateDisabledPosition(keeperPos.id, p.team);
                }
            } else {
                if (p.isGoalkeeper) {
                    p.isGoalkeeper = false;
                    p.position = 0;
                    p.isChanged = true;
                }
            }
        });

        this.syncToResourceStore();
    });

    setPosition = action((playerId, positionId) => {
        const pos = this.allPositions.find((pos) => pos.id === positionId);

        this.allPlayers.forEach((p, index) => {
            if (p.contact === playerId) {
                if (pos?.positionType === "isGoalkeeper") {
                    p.isGoalkeeper = true;
                }

                const oldPos = this.allPositions.find(
                    (old) => old.id === p.position,
                );

                if (oldPos?.isUniquePerGame) {
                    if (oldPos.positionType === "isGoalkeeper") {
                        p.isGoalkeeper = false;
                    }
                    this.updateDisabledPosition(oldPos.id, p.team, true);
                }

                this.allPlayers[index] = {
                    ...p,
                    position: positionId,
                    isChanged: true,
                };

                if (pos?.isUniquePerGame) {
                    this.updateDisabledPosition(pos.id, p.team);
                }
            } else {
                if (
                    p.position === positionId &&
                    pos?.isUniquePerGame &&
                    p.team === this.teamId
                ) {
                    p.position = 0;
                    if (pos.positionType === "isGoalkeeper")
                        p.isGoalkeeper = false;
                    p.isChanged = true;
                }
            }
        });

        this.syncToResourceStore();
    });

    setCaptain = action((playerId) => {
        this.allPlayers.forEach((p) => {
            if (p.team !== this.teamId) return;

            if (p.isCaptain && p.contact !== playerId) {
                p.isCaptain = false;
                p.isChanged = true;
            }

            if (p.contact === playerId) {
                p.isCaptain = true;
                p.isChanged = true;
            }
        });

        this.syncToResourceStore();
    });

    setNumber = action((playerId, number) => {
        const player = this.allPlayers.find((p) => p.contact === playerId);
        if (!player) return;

        const clean = String(number).replace(/\D/g, "").slice(0, 2);

        player.number = clean || null;
        player.isChanged = true;

        this.syncToResourceStore();
    });

    toggleStartingPlayer = action((playerId) => {
        const player = this.allPlayers.find(
            (p) => p.contact === playerId && p.team === this.teamId,
        );

        if (!player) return;

        if (
            !player.isStartingPlayer &&
            this.startingPlayersCount >= this.numberOfStartingPlayers
        )
            return;

        const idx = this.allPlayers.findIndex((p) => p.contact === playerId);

        const updatedPlayer = {
            ...player,
            isStartingPlayer: !player.isStartingPlayer,
            isChanged: true,
        };

        this.allPlayers[idx] = updatedPlayer;

        this.syncToResourceStore();
    });

    removePlayer = action((playerId) => {
        const idx = this.allPlayers.findIndex(
            (p) => p.contact === playerId && p.team === this.teamId,
        );
        if (idx === -1) return;

        const player = this.allPlayers[idx];

        if (player.notSaved) {
            this.allPlayers.splice(idx, 1);
            this.syncToResourceStore();
            return;
        }

        this.updateDisabledPosition(player.position, player.team, true);

        const updatedPlayer = {
            ...player,
            isDeleted: true,
            isChanged: true,
        };
        this.resetPlayer(updatedPlayer);

        this.allPlayers[idx] = updatedPlayer;

        this.syncToResourceStore();
    });

    resetPlayer = action((p) => {
        p.isGoalkeeper = false;
        p.isSubstituteGoalkeeper = false;
        p.isCaptain = false;
        p.isStartingPlayer = false;
        p.position = 0;
        p.number = null;
    });
}

export const playerStore = new PlayerStore();
